import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import type { CatalogItem } from '@/lib/draft-order-client';
import { OptionGroupSelector } from './option-group-selector';
import { QuantityStepper } from './quantity-stepper';
import { lineSubtotal, missingRequiredGroups, type DraftLine, type DraftLineOption } from './cart-draft';

interface ItemEditorDialogProps {
  open: boolean;
  /** The catalog entry being configured. Null while the dialog is closing. */
  item: CatalogItem | null;
  /** Present when editing an existing line rather than adding a new one. */
  line?: DraftLine;
  currencySymbol: string;
  onCancel: () => void;
  onSubmit: (selection: { quantity: number; options: DraftLineOption[]; comments: string }) => void;
}

/**
 * The guided flow for one cart line: quantity, option groups, note.
 *
 * Required groups come first and the submit button stays disabled until every one is satisfied, so
 * the operator is walked through the same choices the customer would have been asked for — rather
 * than saving a line the server will bounce.
 */
export function ItemEditorDialog({ open, item, line, currencySymbol, onCancel, onSubmit }: ItemEditorDialogProps) {
  const { t } = useTranslation('conversations');
  const [quantity, setQuantity] = useState(1);
  const [options, setOptions] = useState<DraftLineOption[]>([]);
  const [comments, setComments] = useState('');

  useEffect(() => {
    if (!open) return;
    setQuantity(line?.quantity ?? 1);
    setOptions(line?.options ?? []);
    setComments(line?.comments ?? '');
  }, [open, line]);

  const groups = useMemo(() => {
    const all = item?.optionGroups ?? [];
    // Required groups first: they are the ones blocking the button, so they should not be buried
    // under a long list of optional extras.
    return [...all].sort((a, b) => Number(b.required) - Number(a.required));
  }, [item]);

  const missing = item ? missingRequiredGroups(item, options) : [];
  const unitPrice = item ? (item.promoPrice ?? item.price) : (line?.unitPrice ?? 0);
  const preview = lineSubtotal({
    key: 'preview',
    catalogItemId: item?.id ?? '',
    name: item?.name ?? '',
    quantity,
    options,
    comments,
    unitPrice,
    orphaned: false,
  });

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onCancel()}>
      <DialogContent className="flex max-h-[85vh] flex-col overflow-hidden sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{item?.name ?? line?.name}</DialogTitle>
        </DialogHeader>

        <div className="flex-1 space-y-5 overflow-y-auto pr-1">
          {item?.description && <p className="text-sm text-muted-foreground">{item.description}</p>}

          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium">{t('cart.editor.quantity')}</Label>
            <QuantityStepper value={quantity} onChange={setQuantity} variant="outline" />
          </div>

          {groups.map((group) => (
            <OptionGroupSelector
              key={group.id}
              group={group}
              selected={options}
              currencySymbol={currencySymbol}
              onChange={setOptions}
            />
          ))}

          <div className="space-y-1.5">
            <Label htmlFor="cart-item-comments" className="text-sm font-medium">
              {t('cart.editor.note')}
            </Label>
            <Textarea
              id="cart-item-comments"
              rows={2}
              value={comments}
              onChange={(event) => setComments(event.target.value)}
              placeholder={t('cart.editor.notePlaceholder')}
            />
          </div>
        </div>

        <DialogFooter className="sm:justify-between">
          <span className="text-sm text-muted-foreground">
            {currencySymbol}
            {preview.toFixed(2)}
          </span>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={onCancel}>
              {t('cart.actions.cancel')}
            </Button>
            <Button
              type="button"
              disabled={missing.length > 0}
              title={
                missing.length > 0
                  ? t('cart.editor.missingGroups', { groups: missing.map((g) => g.name).join(', ') })
                  : undefined
              }
              onClick={() => onSubmit({ quantity, options, comments })}
            >
              {line ? t('cart.actions.apply') : t('cart.actions.add')}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
